import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Loader2,
  CheckCircle,
  AlertCircle,
  Edit2,
  X,
  Save,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin config table for per-state annual / biennial compliance filings.
 * 100 rows = 50 states × {us_llc, us_c_corp}. Edit-only (no create, no delete).
 * Mirrors SuperAdminStateAddresses.jsx layout/UX exactly.
 */
const SuperAdminStateFilings = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Filters
  const [entityFilter, setEntityFilter] = useState('all'); // all | us_llc | us_c_corp
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  // Edit modal state
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Seed button
  const [seeding, setSeeding] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await axios.get(`${API}/admin/state-filings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      setRows(data.state_filings || []);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to load state filings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      filing_name: row.filing_name || '',
      due_date_rule: row.due_date_rule || 'anniversary_month',
      fixed_month: row.fixed_month ?? '',
      fixed_day: row.fixed_day ?? '',
      frequency_years: row.frequency_years || 1,
      fee_type: row.fee_type || 'fixed',
      govt_fee: row.govt_fee ?? 0,
      govt_fee_note: row.govt_fee_note || '',
      platform_fee: row.platform_fee ?? 100,
      is_active: !!row.is_active,
    });
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setForm({});
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean up conditional fields server-side respects
      if (payload.due_date_rule !== 'fixed_date') {
        payload.fixed_month = null;
        payload.fixed_day = null;
      } else {
        payload.fixed_month = parseInt(payload.fixed_month, 10);
        payload.fixed_day = parseInt(payload.fixed_day, 10);
      }
      if (payload.fee_type === 'variable') {
        payload.govt_fee = 0;
      } else {
        payload.govt_fee_note = '';
      }
      payload.frequency_years = parseInt(payload.frequency_years, 10) || 1;
      payload.govt_fee = parseFloat(payload.govt_fee) || 0;
      payload.platform_fee = parseFloat(payload.platform_fee) || 0;

      const { data } = await axios.put(
        `${API}/admin/state-filings/${editing.state_code}/${editing.entity_type}`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      setRows((prev) => prev.map((r) =>
        r.state_code === data.state_code && r.entity_type === data.entity_type ? data : r
      ));
      const entityLabel = data.entity_type === 'us_llc' ? 'LLC' : 'C-Corp';
      showToast(`${data.state_name} ${entityLabel} filing updated`);
      closeEdit();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to save'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/state-filings/seed`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      showToast(data.message || 'Seeded');
      await fetchRows();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Seed failed'), 'error');
    } finally {
      setSeeding(false);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (entityFilter !== 'all' && r.entity_type !== entityFilter) return false;
      if (statusFilter === 'active' && !r.is_active) return false;
      if (statusFilter === 'inactive' && r.is_active) return false;
      return true;
    });
  }, [rows, entityFilter, statusFilter]);

  const entityLabel = (e) => (e === 'us_llc' ? 'LLC' : e === 'us_c_corp' ? 'C-Corp' : e);

  const dueDateDisplay = (r) => {
    if (r.due_date_rule === 'fixed_date' && r.fixed_month && r.fixed_day) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[r.fixed_month - 1]} ${r.fixed_day}`;
    }
    if (r.due_date_rule === 'biennial') return 'Biennial · Anniv.';
    return 'Anniversary Month';
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="superadmin-state-filings-page">
        {toast && (
          <div
            className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
              toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
            }`}
            data-testid="state-filings-toast"
          >
            {toast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
            <span>{toast.message}</span>
          </div>
        )}

        <div className="mb-6">
          <Link
            to="/superadmin/products"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            data-testid="back-to-products-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Products
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#00E87A33' }}>
              <CalendarDays className="w-5 h-5" style={{ color: '#00E87A' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">State Filings</h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Per-state annual / biennial compliance filings — LLC and C-Corp
              </p>
            </div>
          </div>

          {isSuperAdmin && (
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-gray-200 border border-gray-700 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="seed-state-filings-btn"
              title="Create missing state×entity rows (idempotent — never overwrites existing data)"
            >
              <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
              {seeding ? 'Seeding…' : 'Seed State Filings'}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4" data-testid="state-filings-filters">
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wide text-gray-500">Entity</label>
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-[#00E87A]"
              data-testid="filter-entity-type"
            >
              <option value="all">All</option>
              <option value="us_llc">LLC</option>
              <option value="us_c_corp">C-Corp</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wide text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-[#00E87A]"
              data-testid="filter-status"
            >
              <option value="all">All</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
          </div>
          <div className="ml-auto text-xs text-gray-500 self-center" data-testid="state-filings-count">
            Showing {filteredRows.length} of {rows.length}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="state-filings-loading">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg" data-testid="state-filings-error">
            {error}
          </div>
        ) : (
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="state-filings-table">
              <thead className="bg-gray-900/50 text-gray-300">
                <tr>
                  <th className="text-left px-4 py-3 font-medium w-32">State</th>
                  <th className="text-left px-4 py-3 font-medium w-24">Entity</th>
                  <th className="text-left px-4 py-3 font-medium">Filing Name</th>
                  <th className="text-left px-4 py-3 font-medium w-44">Due Date</th>
                  <th className="text-left px-4 py-3 font-medium w-24">Fee Type</th>
                  <th className="text-right px-4 py-3 font-medium w-24">Govt Fee</th>
                  <th className="text-right px-4 py-3 font-medium w-24">Plat. Fee</th>
                  <th className="text-center px-4 py-3 font-medium w-24">Status</th>
                  <th className="text-right px-4 py-3 font-medium w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={`${row.state_code}-${row.entity_type}`}
                    className={`border-t border-gray-700/40 hover:bg-gray-800/40 transition-colors ${
                      row.is_active ? '' : 'opacity-50'
                    }`}
                    data-testid={`state-filing-row-${row.state_code}-${row.entity_type}`}
                  >
                    <td className="px-4 py-3 text-gray-200">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500 w-7">{row.state_code}</span>
                        <span>{row.state_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{entityLabel(row.entity_type)}</td>
                    <td className="px-4 py-3 text-gray-200">{row.filing_name}</td>
                    <td className="px-4 py-3 text-gray-300">{dueDateDisplay(row)}</td>
                    <td className="px-4 py-3 text-gray-300 capitalize">{row.fee_type}</td>
                    <td className="px-4 py-3 text-right text-gray-200 font-mono">
                      {row.fee_type === 'variable' ? '—' : `$${Number(row.govt_fee).toFixed(2)}`}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-200 font-mono">
                      ${Number(row.platform_fee).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-700/40">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-700/40 text-gray-400 border border-gray-700/50">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEdit(row)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 border border-gray-700 hover:bg-gray-700 transition-colors"
                        data-testid={`edit-filing-${row.state_code}-${row.entity_type}-btn`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      No rows match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="state-filing-edit-modal"
          onClick={closeEdit}
        >
          <div
            className="w-full max-w-xl bg-[#0F141B] border border-gray-700 rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {editing.state_name} · {entityLabel(editing.entity_type)}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">{editing.state_code} / {editing.entity_type}</p>
              </div>
              <button
                onClick={closeEdit}
                disabled={saving}
                className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50"
                data-testid="close-edit-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <Field label="Filing Name">
                <input
                  type="text"
                  value={form.filing_name || ''}
                  onChange={(e) => setForm({ ...form, filing_name: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                  data-testid="form-filing-name"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Due Date Rule">
                  <select
                    value={form.due_date_rule}
                    onChange={(e) => setForm({ ...form, due_date_rule: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                    data-testid="form-due-date-rule"
                  >
                    <option value="anniversary_month">Anniversary Month</option>
                    <option value="fixed_date">Fixed Date</option>
                    <option value="biennial">Biennial</option>
                  </select>
                </Field>
                <Field label="Frequency (Years)">
                  <input
                    type="number"
                    min={1}
                    value={form.frequency_years || 1}
                    onChange={(e) => setForm({ ...form, frequency_years: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                    data-testid="form-frequency-years"
                  />
                </Field>
              </div>

              {form.due_date_rule === 'fixed_date' && (
                <div className="grid grid-cols-2 gap-3" data-testid="form-fixed-date-fields">
                  <Field label="Fixed Month (1-12)">
                    <input
                      type="number" min={1} max={12}
                      value={form.fixed_month ?? ''}
                      onChange={(e) => setForm({ ...form, fixed_month: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                      data-testid="form-fixed-month"
                    />
                  </Field>
                  <Field label="Fixed Day (1-31)">
                    <input
                      type="number" min={1} max={31}
                      value={form.fixed_day ?? ''}
                      onChange={(e) => setForm({ ...form, fixed_day: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                      data-testid="form-fixed-day"
                    />
                  </Field>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Fee Type">
                  <select
                    value={form.fee_type}
                    onChange={(e) => setForm({ ...form, fee_type: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                    data-testid="form-fee-type"
                  >
                    <option value="fixed">Fixed</option>
                    <option value="variable">Variable</option>
                  </select>
                </Field>
                <Field label="Platform Fee (USD)">
                  <input
                    type="number" step="0.01" min={0}
                    value={form.platform_fee ?? 0}
                    onChange={(e) => setForm({ ...form, platform_fee: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                    data-testid="form-platform-fee"
                  />
                </Field>
              </div>

              {form.fee_type === 'fixed' && (
                <Field label="Govt Fee (USD)">
                  <input
                    type="number" step="0.01" min={0}
                    value={form.govt_fee ?? 0}
                    onChange={(e) => setForm({ ...form, govt_fee: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                    data-testid="form-govt-fee"
                  />
                </Field>
              )}

              {form.fee_type === 'variable' && (
                <Field label="Govt Fee Note (shown to client)">
                  <textarea
                    rows={3}
                    value={form.govt_fee_note || ''}
                    onChange={(e) => setForm({ ...form, govt_fee_note: e.target.value })}
                    placeholder="e.g. Based on authorized shares. Confirmed before filing."
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-[#00E87A]"
                    data-testid="form-govt-fee-note"
                  />
                </Field>
              )}

              <Field label="">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-[#00E87A] focus:ring-[#00E87A] focus:ring-offset-0"
                    data-testid="form-is-active"
                  />
                  <span className="text-sm text-gray-200">
                    Active{' '}
                    <span className="text-xs text-gray-500">
                      — when ON, this filing is visible to clients and an annual_report product is auto-generated.
                    </span>
                  </span>
                </label>
              </Field>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-700/60">
              <button
                onClick={closeEdit}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-300 border border-gray-700 hover:bg-gray-800 transition-colors disabled:opacity-50"
                data-testid="cancel-edit-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-gray-900 disabled:opacity-50"
                style={{ backgroundColor: '#00E87A' }}
                data-testid="save-filing-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

const Field = ({ label, children }) => (
  <div>
    {label && <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">{label}</label>}
    {children}
  </div>
);

export default SuperAdminStateFilings;
